import React, { useState, useEffect } from 'react';
import {
  Box, TextField, MenuItem, Select, InputLabel, FormControl,
  Autocomplete, Popover, TablePagination
} from '@mui/material';

const C = {
  bg: '#FAFAF9', surface: '#FFFFFF', surface2: '#F6F6F4',
  ink1: '#0B1020', ink2: '#1F2937', ink3: '#475569', ink4: '#64748B', ink5: '#94A3B8',
  line: '#E7E5E4', line2: '#EFEDEA',
  indigo: '#4F46E5', indigo50: '#EEF2FF',
  emerald: '#059669', emerald50: '#ECFDF5',
  amber: '#D97706', rose: '#E11D48',
  shadow: '0 1px 2px rgba(15,23,42,.04), 0 6px 20px -8px rgba(15,23,42,.08)',
  shadowSm: '0 1px 2px rgba(15,23,42,.04)',
};

const f = {
  display: "'Inter Tight','Inter',sans-serif",
  body: "'Inter',system-ui,sans-serif",
  mono: "'JetBrains Mono',ui-monospace,monospace",
};

const STATUS_MAP = {
  ok:              { label: 'OK',           bg: '#ECFDF5', color: '#059669', border: '#A7F3D0' },
  ok_global:       { label: 'OK Global',    bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' },
  ok_with_warning: { label: 'OK (Warn)',    bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
  expired:         { label: 'Expired',      bg: '#FFF1F2', color: '#E11D48', border: '#FECDD3' },
  not_found:       { label: 'Not Found',    bg: '#F5F3FF', color: '#7C3AED', border: '#DDD6FE' },
  denied_global:   { label: 'Denied',       bg: '#FFF1F2', color: '#E11D48', border: '#FECDD3' },
};

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || { label: status || '—', bg: C.surface2, color: C.ink4, border: C.line };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px',
      borderRadius: 999, fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap',
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontFamily: f.body,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
      {s.label}
    </span>
  );
}

const TH = ({ children }) => (
  <th style={{
    textAlign: 'left', fontSize: 10.5, color: C.ink4, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '.05em', padding: '9px 12px',
    borderBottom: `1px solid ${C.line}`, background: '#FCFCFB',
    fontFamily: f.body, whiteSpace: 'nowrap',
  }}>{children}</th>
);

function td(extra) {
  return { padding: '9px 12px', borderBottom: `1px solid ${C.line2}`, verticalAlign: 'middle', ...extra };
}

export default function HistoryTab({ apiBase, token }) {
  const [data, setData] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const _initParams = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    licenseId: _initParams.get("h_licenseId") || '',
    appVersion: _initParams.get("h_appVersion") || '',
    ipAddress: _initParams.get("h_ipAddress") || '',
    status: _initParams.get("h_status") || '',
    startTime: _initParams.get("h_startTime") || '',
    endTime: _initParams.get("h_endTime") || '',
  });

  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const handleDateClick = (event) => setDateAnchorEl(event.currentTarget);
  const handleDateClose = () => setDateAnchorEl(null);

  const displayDateText = () => {
    if (filters.startTime && filters.endTime) return `${filters.startTime.replace('T', ' ')} – ${filters.endTime.replace('T', ' ')}`;
    if (filters.startTime) return `From: ${filters.startTime.replace('T', ' ')}`;
    if (filters.endTime) return `To: ${filters.endTime.replace('T', ' ')}`;
    return 'Date Range';
  };

  useEffect(() => {
    if (apiBase && token) {
      fetch(`${apiBase}/licenses`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          setLicenses(list);
          const paramId = _initParams.get("h_licenseId");
          if (paramId) {
            const found = list.find(l => String(l.id) === paramId);
            if (found) setSelectedLicense(found);
          }
        })
        .catch(console.error);
    }
    // eslint-disable-next-line
  }, [apiBase, token]);

  const fetchHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const queryParams = new URLSearchParams({ limit: rowsPerPage, offset: page * rowsPerPage });
      if (filters.licenseId) queryParams.append('licenseId', filters.licenseId);
      if (filters.appVersion) queryParams.append('appVersion', filters.appVersion);
      if (filters.ipAddress) queryParams.append('ipAddress', filters.ipAddress);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.startTime) queryParams.append('startTime', new Date(filters.startTime).toISOString());
      if (filters.endTime) queryParams.append('endTime', new Date(filters.endTime).toISOString());

      const res = await fetch(`${apiBase}/history/paginated?${queryParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed to fetch history'); }
      const responseData = await res.json();
      setData(responseData.data || []);
      setTotal(responseData.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiBase && token) fetchHistory();
    // eslint-disable-next-line
  }, [page, rowsPerPage, apiBase, token]);

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const handleApplyFilters = () => { setPage(0); fetchHistory(); };

  const handleClearFilters = () => {
    setFilters({ licenseId: '', appVersion: '', ipAddress: '', status: '', startTime: '', endTime: '' });
    setSelectedLicense(null);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const keys = { licenseId: "h_licenseId", appVersion: "h_appVersion", ipAddress: "h_ipAddress", status: "h_status", startTime: "h_startTime", endTime: "h_endTime" };
    Object.entries(keys).forEach(([fKey, pKey]) => {
      if (filters[fKey]) params.set(pKey, filters[fKey]); else params.delete(pKey);
    });
    window.history.replaceState(null, "", `?${params.toString()}`);
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (Object.values(filters).every(x => x === '') && apiBase) fetchHistory();
    // eslint-disable-next-line
  }, [filters]);

  const hasDateFilter = filters.startTime || filters.endTime;

  return (
    <>
      <style>{`@keyframes lm-spin { to { transform: rotate(360deg); } } .lm-ht tr:hover td { background: #FDFCFB; }`}</style>
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '18px 20px 60px', background: C.bg, minHeight: '100%' }}>

        {/* page header */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11.5, color: C.ink5, marginBottom: 3 }}>
            License Manager &nbsp;/&nbsp; <b style={{ color: C.ink3, fontWeight: 500 }}>History</b>
          </div>
          <h1 style={{ fontFamily: f.display, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.02em', color: C.ink1 }}>
            Validation History
          </h1>
        </div>

        {/* filter bar */}
        <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 16px', boxShadow: C.shadowSm, marginBottom: 12 }}>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
            <Autocomplete
              options={licenses}
              getOptionLabel={(opt) => `${opt.clientName} (ID: ${opt.id})`}
              value={selectedLicense}
              onChange={(_, newValue) => {
                setSelectedLicense(newValue);
                setFilters(f => ({ ...f, licenseId: newValue ? newValue.id : '' }));
              }}
              renderInput={(params) => <TextField {...params} label="Client Name" size="small" />}
              sx={{ width: 220 }}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
            <TextField label="App Version" name="appVersion" size="small" value={filters.appVersion} onChange={handleFilterChange} sx={{ width: 120 }} />
            <TextField label="IP Address" name="ipAddress" size="small" value={filters.ipAddress} onChange={handleFilterChange} sx={{ width: 140 }} />
            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select name="status" value={filters.status} label="Status" onChange={handleFilterChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="ok">OK</MenuItem>
                <MenuItem value="ok_global">OK (Global)</MenuItem>
                <MenuItem value="ok_with_warning">OK (Warning)</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="not_found">Not Found</MenuItem>
                <MenuItem value="denied_global">Denied (Global)</MenuItem>
              </Select>
            </FormControl>
            <button onClick={handleDateClick} style={{
              height: 40, padding: '0 12px', borderRadius: 7,
              border: `1px solid ${hasDateFilter ? C.indigo : C.line}`,
              background: hasDateFilter ? C.indigo50 : C.surface,
              color: hasDateFilter ? C.indigo : C.ink3,
              cursor: 'pointer', fontSize: 12, fontFamily: f.body, fontWeight: 500,
            }}>
              {displayDateText()}
            </button>
            <Popover open={Boolean(dateAnchorEl)} anchorEl={dateAnchorEl} onClose={handleDateClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
              <Box p={2} display="flex" flexDirection="column" gap={2}>
                <TextField label="Start Time" name="startTime" type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={filters.startTime} onChange={handleFilterChange} />
                <TextField label="End Time" name="endTime" type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={filters.endTime} onChange={handleFilterChange} />
              </Box>
            </Popover>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <button onClick={handleApplyFilters} style={{
                height: 36, padding: '0 16px', borderRadius: 7,
                border: `1px solid ${C.indigo}`, background: C.indigo,
                color: '#fff', cursor: 'pointer', fontSize: 12, fontFamily: f.body, fontWeight: 500,
              }}>Search</button>
              <button onClick={handleClearFilters} style={{
                height: 36, padding: '0 14px', borderRadius: 7,
                border: `1px solid ${C.line}`, background: C.surface,
                color: C.ink3, cursor: 'pointer', fontSize: 12, fontFamily: f.body, fontWeight: 500,
              }}>Clear</button>
            </Box>
          </Box>
        </div>

        {error && (
          <div style={{ background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 8, padding: '10px 14px', color: C.rose, fontSize: 12, marginBottom: 10 }}>
            {error}
          </div>
        )}

        {/* table card */}
        <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, boxShadow: C.shadowSm, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="lm-ht" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <TH>ID</TH>
                  <TH>Time</TH>
                  <TH>License</TH>
                  <TH>App</TH>
                  <TH>Version</TH>
                  <TH>IP Address</TH>
                  <TH>Local IP</TH>
                  <TH>Status</TH>
                  <TH>Message</TH>
                  <TH>Detail</TH>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} style={{ textAlign: 'center', padding: '40px 0' }}>
                      <div style={{ display: 'inline-block', width: 24, height: 24, border: `3px solid ${C.line}`, borderTopColor: C.indigo, borderRadius: '50%', animation: 'lm-spin .9s linear infinite' }} />
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ textAlign: 'center', padding: '40px 0', color: C.ink5, fontSize: 12 }}>
                      No history found.
                    </td>
                  </tr>
                ) : data.map(row => (
                  <tr key={row.id}>
                    <td style={td({ fontFamily: f.mono, fontSize: 11, color: C.ink3 })}>#{String(row.id).padStart(4, '0')}</td>
                    <td style={td({ fontFamily: f.mono, fontSize: 11, color: C.ink3, whiteSpace: 'nowrap' })}>{row.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                    <td style={td({ fontWeight: 500, color: C.ink1 })}>{row.clientName}</td>
                    <td style={td({ color: C.ink2 })}>{row.appName}</td>
                    <td style={td({ fontFamily: f.mono, fontSize: 11, color: C.ink3 })}>{row.appVersion}</td>
                    <td style={td({ fontFamily: f.mono, fontSize: 11, color: C.ink3 })}>{row.ipAddress}</td>
                    <td style={td({ fontFamily: f.mono, fontSize: 11, color: C.ink4 })}>{row.localIp}</td>
                    <td style={td({})}><StatusPill status={row.status} /></td>
                    <td style={td({ fontSize: 11.5, color: C.rose, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })} title={row.errorMessage}>{row.errorMessage}</td>
                    <td style={td({ fontSize: 11.5, color: C.ink3, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })} title={row.detail}>{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ borderTop: `1px solid ${C.line}` }}>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ color: C.ink3, fontSize: 12 }}
            />
          </div>
        </div>

      </div>
    </>
  );
}
